import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { Badge } from 'Components/_theme'
import type { CardDetailsListItem } from 'Components/CardDetailsList/CardDetailsList.types'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { OrderStatusBadge } from 'Components/OrderStatusBadge/OrderStatusBadge'
import { PresentationCard } from 'Components/PresentationCard/PresentationCard'

import { useIsOrganisation } from 'Hooks'

import type { DeviceTypeEnum } from 'Types/deviceTypes'

import type { OrderPresentationCardProps } from './OrderPresentationCard.types'

export const OrderPresentationCard = (props: OrderPresentationCardProps) => {
  const {
    status,
    requestedRentCategory,
    requestedOfferOrder,
    deviceDefinition,
    customOrderDeviceName,
    isPurchase,
  } = props

  const isOrganisation = useIsOrganisation()

  const generatePresentationCardListItems = useMemo(
    (): CardDetailsListItem[] => [
      {
        label: 'RequestDetails_rent_category',
        value: requestedRentCategory?.rentCategory?.name,
      },
      {
        label: isPurchase
          ? 'NewOrder_one_time_copayment'
          : 'RequestDetails_monthly_copayment',
        value: (
          <FormattedCurrency
            value={
              requestedRentCategory?.rentCategory?.copaymentNet ??
              requestedOfferOrder?.copaymentPrice ??
              0
            }
          />
        ),
      },
      ...(isOrganisation && isPurchase
        ? [
            {
              label: 'NewOrder_one_time_purchase_amount',
              value: (
                <FormattedCurrency
                  value={
                    requestedRentCategory?.rentCategory?.displayedBasisAmount ??
                    requestedOfferOrder?.basePrice ??
                    0
                  }
                />
              ),
            },
          ]
        : []),
    ],
    [
      requestedRentCategory?.rentCategory?.name,
      requestedRentCategory?.rentCategory?.copaymentNet,
      requestedRentCategory?.rentCategory?.displayedBasisAmount,
      isPurchase,
      requestedOfferOrder?.copaymentPrice,
      requestedOfferOrder?.basePrice,
      isOrganisation,
    ],
  )

  const deviceData = deviceDefinition || requestedRentCategory?.deviceDefinition

  if (!deviceData) {
    return null
  }

  const header = customOrderDeviceName || deviceData.name

  return (
    <PresentationCard
      header={header}
      imagePlaceholderType={deviceData.productType as DeviceTypeEnum}
      imageSource={deviceData.imageURL}
      listItems={generatePresentationCardListItems}
      rightContent={
        <span>
          <OrderStatusBadge status={status} />
          {isPurchase && (
            <Badge className="ms-2" color="yellow" rounded variant="secondary">
              <FormattedMessage id="NewOrder_purchase" />
            </Badge>
          )}
        </span>
      }
      title="RequestDetails_device"
    />
  )
}
